import {
  Avatar,
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useTheme } from '../../contexts/ThemeProvider';
import { useMemo, useRef } from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import { badges } from '../../pages/Profile/components/Plans';
import { Text } from '../atoms/Text';
import { useProfile } from '@/contexts/ProfileProvider';
import { getAgreementPlans } from '@/service/subscription/api';
import { useAPICallAsQuery } from '@/service/hooks';

export function ProfileCard() {
  const { profile, updateProfileImage, isLoading, subscriptions } = useProfile();
  const { id, colors, getValidSubscriptions, isApp, isProductSuperApp, product } = useTheme();
  const { data: plans } = useAPICallAsQuery('plans', getAgreementPlans, { agreementID: id?.toString() || '0' });

  const { isOpen, onOpen, onClose } = useDisclosure();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const disabledLevel = useMemo(() => {
    const validSubscriptions = getValidSubscriptions(subscriptions);

    if (isProductSuperApp) {
      return validSubscriptions.find((subscription: any) => subscription.plan.product === product?.id)?.plan.id;
    } else {

      if (validSubscriptions.length === 0) return -1;

      return validSubscriptions[0].plan.id;
    }

  }, [subscriptions, getValidSubscriptions]);

  const plan = isProductSuperApp && product ? product?.plans?.find((plan: any) => plan.id === disabledLevel) : plans?.find((plan: any) => plan.id === disabledLevel);


  if (!profile) return <></>;

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      updateProfileImage(file);
    }
  };

  const askPermission = async () => {
    if (!localStorage.getItem('camera_permission') && isApp) {
      onOpen();
    } else {
      inputRef.current?.click();
    }
  };

  return (
    <HStack
      marginBottom={{
        base: '20px',
        lg: '0px',
      }}
      align={'center'}
      width={{
        base: '100%',
        lg: 'auto',
      }}
      minWidth={{
        base: '300px',
      }}
      spacing={4}
    >
      <Box position={'relative'} backgroundColor={'white'} borderRadius={'100px'} padding="3px">
        <Avatar size={'lg'} src={profile.image} />
        <Box position="absolute" bottom="-10px" right="-5px">
          {isLoading ? (
            <Spinner color={colors.primaryColor} />
          ) : (
            <IconButton
              icon={<FaPencilAlt color={colors.primaryColor} />}
              size="sm"
              aria-label="Edit profile picture"
              onClick={askPermission}
            />
          )}
          <Input type="file" accept="image/*" ref={inputRef} display="none" onChange={handleImageChange} />
        </Box>
      </Box>
      <VStack spacing={1} align={'flex-start'}>
        <Heading lineHeight={'normal'} color={'#292927'} fontFamily={'Glory'} fontWeight={700} fontSize={'28px'}>
          Olá, {profile.fullName}
        </Heading>
        {plan && !isProductSuperApp && (
          <HStack alignItems={'center'} spacing={2} align={'flex-start'}>
            {badges.find((b) => b.level === plan.level || b.level === plan.level?.rank) && (
              <Image src={`/${badges.find((b) => b.level === plan.level || b.level === plan.level?.rank)?.image}`} alt={plan.name} height={'20px'} />
            )}
            <Heading color={colors.primaryColor} fontSize={'14px'} fontWeight={500}>
              Categoria {plan?.name}
            </Heading>
          </HStack>
        )}
      </VStack>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Permissão de Uso</ModalHeader>
          <ModalBody>
            <Text>
              Precisamos de sua permissão para acessar sua câmera e galeria de fotos para que você possa atualizar sua
              foto de perfil. assim ter uma experiência mais personalizada.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              backgroundColor={colors.primaryColor}
              _hover={{ backgroundColor: colors.primaryColor }}
              onClick={() => {
                inputRef.current?.click();
                localStorage.setItem('camera_permission', 'true');
                onClose();
              }}
            >
              Continuar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </HStack>
  );
}
